import React, { useCallback, useMemo } from 'react';
import { TreeEntry, TreePath } from './types';
import { TreeList } from './TreeList';
import { TreeContext } from './context';
import { useTreeCheckbox, useTreeRover, useTreeSearch } from './hooks';

export interface TreeProps {
  items: TreeEntry[];
  searchKeyword?: string;
  expandedIds?: string[];
  defaultExpandedIds?: string[];
  selectedIds?: string[];
  /** @default false */
  checkable?: boolean;
  checkedIds?: string[];
  /**
   * 항목 선택 시 확장/접힘을 함께 처리할지 여부.
   * `false`로 두면 항목(행) 클릭은 선택만 하고, 확장/접힘은 좌측 화살표 아이콘 클릭(또는 키보드 방향키)으로만 동작한다.
   * @default true
   */
  shouldExpandOnSelect?: boolean;
  onSelect?: (item: TreeEntry, path: TreePath) => void;
  /** 체크된 항목이 변경될 때 호출되는 함수 */
  onCheckedChange?: (items: TreePath, checkedIds: string[]) => void;
  onExpand?: (item: TreeEntry, path: TreePath) => void;
}

export const Tree: React.FC<TreeProps> = ({
  items,
  searchKeyword = '',
  checkable = false,
  shouldExpandOnSelect = true,
  selectedIds,
  checkedIds = [],
  expandedIds,
  defaultExpandedIds = selectedIds,
  onSelect,
  onCheckedChange,
  onExpand: onExpandProp,
}) => {
  const {
    onExpand: onExpandState,
    expandedIds: expandedIdsState,
    items: filteredItems,
  } = useTreeSearch({ searchKeyword, expandedIds, defaultExpandedIds, items });

  const { handleCheck, getCheckState } = useTreeCheckbox<TreeEntry>({
    items,
    checkedIds,
    checkable,
    onChecked: onCheckedChange,
  });

  const selectedIdsSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const checkedIdsSet = useMemo(() => new Set(checkedIds), [checkedIds]);

  const handleSelect = useCallback(
    (item: TreeEntry, path: TreePath) => {
      onSelect?.(item, path);
      if (shouldExpandOnSelect) {
        onExpandProp?.(item, path);
        onExpandState(item);
      }
    },
    [onSelect, onExpandProp, onExpandState, shouldExpandOnSelect],
  );

  const handleExpand = useCallback(
    (item: TreeEntry, path: TreePath) => {
      onExpandProp?.(item, path);
      onExpandState(item);
    },
    [onExpandProp, onExpandState],
  );
  const { getRoverItemProps } = useTreeRover({
    items: filteredItems,
    expandedIds: expandedIdsState,
    checkable,
    onSelect: handleSelect,
    onExpand: handleExpand,
    onCheck: handleCheck,
    getCheckState,
  });

  return (
    <TreeContext.Provider
      value={{
        selectedIds: selectedIdsSet,
        checkedIds: checkedIdsSet,
        expandedIds: expandedIdsState,
        checkable,
        shouldExpandOnSelect,
        onSelect: handleSelect,
        onExpand: handleExpand,
        onCheck: handleCheck,
        getCheckState,
        getRoverItemProps,
      }}
    >
      <TreeList items={filteredItems} path={[]} role='tree' />
    </TreeContext.Provider>
  );
};
